import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowUp, ArrowRight, ArrowLeft, Pencil, Paperclip, X } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import HomeMarketing from "@/components/home/HomeMarketing";
import Reveal from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";
import { consumeConversationRequest } from "@/lib/conversation";
import { HOME, DISCOVERY } from "@/constants/testIds";
import RecommendationView from "@/components/RecommendationView";
import HeroIntelligence from "@/components/HeroIntelligence";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "bnm.discovery.v2";

// ---------- Rotating idea examples (landing only) ----------
const PLACEHOLDER_EXAMPLES = [
  "Launch a new brand.",
  "Produce a fashion campaign.",
  "Tell our story.",
  "Make a film people remember.",
  "Create an unforgettable event.",
  "Launch our product.",
  "Produce a music video.",
  "Build our next campaign.",
  "Reimagine our brand.",
];

// Producer processing states. Shown one at a time in the status pill while the
// recommendation is being prepared. Nothing else on screen moves.
const PROCESSING_STATES = [
  "Thinking\u2026",
  "Reviewing your project\u2026",
  "Sketching possibilities\u2026",
  "Building your blueprint\u2026",
  "Almost ready\u2026",
];

// ---------- The conversation script ----------
// Each entry is one turn: the producer response line (2 lines max) + the next
// question that goes with it. `chips` sits above the input. `placeholder` is
// the hint text inside the SAME input (never a new field).
const QUESTIONS = [
  {
    key: "concept_ready",
    responseFor: "brief", // uses previous answer only for logic below
    response: () => "Love it.",
    question: "Do you have a concept ready?",
    chips: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ],
    placeholder: "Type your answer",
    // choice-only: submit through chip only
    inputAllowed: false,
  },
  {
    key: "timeline",
    responseFor: "concept_ready",
    response: (v) =>
      v === "no" ? "No problem. We\u2019ll help you develop the idea." : "Good.",
    question: "When would you like to bring this to life?",
    chips: [
      { value: "ASAP", label: "ASAP" },
      { value: "Within 2 Weeks", label: "Within 2 Weeks" },
      { value: "This Month", label: "This Month" },
      { value: "Just Exploring", label: "Just Exploring" },
    ],
    placeholder: "Or type your own timeline",
    inputAllowed: true,
  },
  {
    key: "budget",
    responseFor: "timeline",
    response: () => "Thanks. That gives us a good starting point.",
    question: "What\u2019s your approximate budget?",
    chips: [
      { value: "Under \u20b91,00,000", label: "Under \u20b91,00,000" },
      { value: "\u20b91,00,000 to \u20b93,00,000", label: "\u20b91L to \u20b93L" },
      { value: "\u20b93,00,000 to \u20b910,00,000", label: "\u20b93L to \u20b910L" },
      { value: "\u20b910,00,000+", label: "\u20b910L and above" },
      { value: "Not sure yet", label: "Not sure yet" },
    ],
    placeholder: "Or type your own amount",
    inputAllowed: true,
  },
  {
    key: "location",
    responseFor: "budget",
    response: () => "Perfect.",
    question: "Where would you like this project to happen?",
    chips: [],
    placeholder: "A city, a country, a specific location.",
    inputAllowed: true,
  },
  {
    key: "notes",
    responseFor: "location",
    response: () => "Perfect.",
    question: "Anything else you\u2019d like us to know?",
    chips: [],
    placeholder: "",
    inputAllowed: true,
    optional: true,
  },
];

const SUMMARY_ROWS = [
  { key: "brief", label: "Project" },
  { key: "concept_ready", label: "Concept" },
  { key: "timeline", label: "Timeline" },
  { key: "budget", label: "Budget" },
  { key: "location", label: "Location" },
  { key: "notes", label: "Additional notes" },
];

// ---------- Storage helpers ----------
function loadPersisted() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function persist(state) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // storage unavailable (private mode); persistence is optional
  }
}
function clearPersisted() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // storage unavailable (private mode); persistence is optional
  }
}

function displayValue(key, val) {
  if (!val || String(val).trim().length === 0) return "\u2013";
  if (key === "concept_ready") return val === "yes" ? "Yes" : val === "no" ? "No" : "\u2013";
  return String(val);
}

// Hero copy renderer, hoisted so React doesn't remount it on every parent render.
function renderHeroCopy(phase, producerVoice, producerQuestion) {
  if ((phase === "question" || phase === "clarify") && producerQuestion) {
    // The producer response line is now rendered above the hero as a small caption
    // (see the eyebrow slot in the Landing JSX). The H1 shows ONLY the question so
    // it stays the primary focus.
    return producerQuestion;
  }
  if (phase === "summary") {
    return (
      <>
        <span className="block">Here{"\u2019"}s what we{"\u2019"}ve</span>
        <span className="block font-light font-serif-italic tracking-[-0.02em]">understood.</span>
      </>
    );
  }
  if (phase === "signin") {
    return (
      <>
        <span className="block">Sign in to see our</span>
        <span className="block font-light font-serif-italic tracking-[-0.02em]">recommendation.</span>
      </>
    );
  }
  if (phase === "preparing") {
    return (
      <>
        <span className="block font-light font-serif-italic tracking-[-0.02em]">Shaping your</span>
        <span className="block">recommendation.</span>
      </>
    );
  }
  if (phase === "error") {
    return (
      <>
        <span className="block">Your Producer is taking a</span>
        <span className="block font-light font-serif-italic tracking-[-0.02em]">moment.</span>
      </>
    );
  }
  return (
    <>
      What would you like{" "}
      <br className="hidden sm:block" />
      to <span className="font-serif-italic bnm-denim-shine font-light tracking-[-0.02em]">create</span> today
      <span className="font-serif-italic text-ink font-light">?</span>
    </>
  );
}

// ---------- Status line ----------
// Anchored below the input in EVERY phase where the input is visible.
// Never moves. Never changes height. Only the text fades between states.
function StatusLine({ text }) {
  return (
    <div
      className="h-[24px] flex items-center justify-center overflow-hidden"
      data-testid="input-status"
      aria-live="polite"
    >
      <span
        key={text}
        className="inline-flex items-center gap-2 eyebrow eyebrow-denim animate-[fadeUp_260ms_ease-out_both] whitespace-nowrap"
      >
        <span className="rec-pulse shrink-0" />
        <span className="leading-none">{text}</span>
      </span>
    </div>
  );
}

// ---------- Recommendation view ----------
export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // The whole flow is driven by a compact phase state:
  //   landing -> question(0..4) -> summary -> signin -> preparing -> recommendation
  //   (error state possible at any point)
  const [phase, setPhase] = useState("landing");
  const [stepIndex, setStepIndex] = useState(0); // 0..4 for Q2..Q6 during "question" phase
  const [clarify, setClarify] = useState(null); // {question, options[]} intent check before Q2
  const [observation, setObservation] = useState(null); // proactive Ida note at summary
  const observeTriedRef = useRef(false);

  // Proactive Ida: one valuable interjection when the summary appears, or silence.
  useEffect(() => {
    if (phase !== "summary" || observeTriedRef.current) return;
    observeTriedRef.current = true;
    axios
      .post(`${API}/producer/observe`, { ...answers })
      .then(({ data }) => {
        if (data?.worth_saying && data.note) setObservation(data.note);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);
  const [savedProject, setSavedProject] = useState(null);

  const [answers, setAnswers] = useState({
    brief: "",
    concept_ready: "",
    timeline: "",
    budget: "",
    location: "",
    notes: "",
  });

  // The single input holds either the initial brief or the free-text answer to
  // the current question. It's the same field, its value is reset when the
  // producer moves on.
  const [inputValue, setInputValue] = useState("");
  const [focused, setFocused] = useState(false);
  const [phIndex, setPhIndex] = useState(0);
  const [phFading, setPhFading] = useState(false);
  // Modal shown when the client clicks the BRAND NEW MONEY logo mid-conversation.
  const [homeConfirmOpen, setHomeConfirmOpen] = useState(false);

  // Producer voice being shown right now. When we advance to the next question
  // we swap `producerVoice` + `producerQuestion` atomically.
  const [producerVoice, setProducerVoice] = useState(""); // top line (response)
  const [producerQuestion, setProducerQuestion] = useState(""); // next question

  const [recommendation, setRecommendation] = useState(null);
  const [error, setError] = useState(null);
  // Brief attachments: references the client hands to Your Producer.
  const [attachments, setAttachments] = useState([]);
  const [uploadingCount, setUploadingCount] = useState(0);
  const fileInputRef = useRef(null);
  // Index into PROCESSING_STATES while the recommendation is being prepared.
  const [processingIndex, setProcessingIndex] = useState(0);

  const inputRef = useRef(null);
  const hasResumedRef = useRef(false);
  const editingFromSummaryRef = useRef(false);
  const producerAckOverrideRef = useRef(null);

  // ---------- Persistence + OAuth resume ----------
  useEffect(() => {
    if (hasResumedRef.current) return;
    hasResumedRef.current = true;
    const saved = loadPersisted();
    if (!saved) return;
    if (saved.answers) setAnswers({ ...saved.answers });
    if (saved.editingFromSummary) editingFromSummaryRef.current = true;

    if (saved.phase === "awaiting-signin" || saved.phase === "signin") {
      if (user) {
        setPhase("preparing");
        submitProject(saved.answers || {});
      } else {
        setPhase("signin");
      }
    } else if (saved.phase === "summary") {
      setPhase("summary");
    } else if (saved.phase === "question" && typeof saved.stepIndex === "number") {
      const q = QUESTIONS[saved.stepIndex];
      if (q) {
        const prev = saved.answers ? saved.answers[q.responseFor] : "";
        setProducerVoice(typeof q.response === "function" ? q.response(prev) : q.response);
        setProducerQuestion(q.question);
      }
      setStepIndex(saved.stepIndex);
      setPhase("question");
    }
  }, [user]);

  useEffect(() => {
    if (phase === "landing" || phase === "recommendation" || phase === "preparing" || phase === "signin") return;
    persist({
      phase,
      stepIndex,
      answers,
      editingFromSummary: editingFromSummaryRef.current,
    });
  }, [phase, stepIndex, answers]);

  useEffect(() => {
    if (!user) return;
    if (phase !== "signin") return;
    setPhase("preparing");
    submitProject(answers);
  }, [user, phase]);

  // Rotating placeholder on landing only
  useEffect(() => {
    if (phase !== "landing") return undefined;
    if (focused || inputValue.length > 0) return undefined;
    const t = setInterval(() => {
      setPhFading(true);
      setTimeout(() => {
        setPhIndex((i) => (i + 1) % PLACEHOLDER_EXAMPLES.length);
        setPhFading(false);
      }, 320);
    }, 3600);
    return () => clearInterval(t);
  }, [phase, focused, inputValue]);

  // While preparing, the ONE status line walks through the processing states
  // and settles on the last one. Nothing else on screen moves.
  useEffect(() => {
    if (phase !== "preparing") {
      setProcessingIndex(0);
      return undefined;
    }
    const t = setInterval(() => {
      setProcessingIndex((i) => Math.min(i + 1, PROCESSING_STATES.length - 1));
    }, 2200);
    return () => clearInterval(t);
  }, [phase]);

  // ---------- Helpers ----------
  const focusInput = useCallback(() => {
    setTimeout(() => inputRef.current?.focus(), 300);
  }, []);

  // One destination: any CTA (here or on another page) brings the visitor back
  // to the conversation. Optionally pre-fills the input with a starting line.
  // Focus places the cursor at the end so the visitor can type immediately.
  const focusCreateBox = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    const len = el.value.length;
    try {
      el.setSelectionRange(len, len);
    } catch {
      // some input types do not support selection ranges; focus is enough
    }
  }, []);

  const startConversation = useCallback((text) => {
    if (typeof text === "string" && text.length > 0) setInputValue(text);
    window.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(focusCreateBox, 550);
  }, [focusCreateBox]);

  // Consume a conversation request left by another page (Services cards,
  // "Start your project" CTAs). Never clobbers a saved mid-conversation state.
  useEffect(() => {
    const req = consumeConversationRequest();
    if (!req) return;
    if (loadPersisted()) return;
    if (req.text) setInputValue(req.text);
    window.scrollTo(0, 0);
    setTimeout(focusCreateBox, 450);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Silent pause between questions. Does NOT change the status pill.
  // Only the LLM preparation phase (after Q6 + Continue) shows the
  // "REVIEWING YOUR PROJECT" state (see statusText below).
  const withPause = useCallback((then) => {
    const t = setTimeout(() => {
      then?.();
    }, 400);
    return () => clearTimeout(t);
  }, []);

  // Advance the producer voice + question to `nextStepIndex` given the answer
  // that was just recorded for the previous key.
  const showQuestion = (nextStepIndex, updatedAnswers) => {
    const q = QUESTIONS[nextStepIndex];
    if (!q) return;
    const prev = updatedAnswers[q.responseFor];
    const override = producerAckOverrideRef.current;
    producerAckOverrideRef.current = null;
    const voice = override || (typeof q.response === "function" ? q.response(prev) : q.response);
    setProducerVoice(voice);
    setProducerQuestion(q.question);
    setStepIndex(nextStepIndex);
    setPhase("question");
    // Reset the shared input for the new question, unless we're returning from
    // an Edit-from-Summary in which case we prefill with the existing answer.
    const currentValue = updatedAnswers[q.key];
    setInputValue(typeof currentValue === "string" ? currentValue : "");
    focusInput();
  };

  // ---------- Handlers ----------
  // Before the questions begin, make sure we truly understood the brief.
  // Ambiguity ("5 products" vs "5 brands") gets one clarifying question with options.
  const beginClarify = async (next) => {
    try {
      const { data } = await axios.post(`${API}/producer/clarify`, {
        idea: next.brief,
        turns: (next.clarifications || []).map((c) => ({ question: c.question, answer: c.answer })),
      });
      if (!data.ready && data.question) {
        setClarify({ question: data.question, options: data.options || [] });
        setProducerVoice("One quick check so I plan this right.");
        setProducerQuestion(data.question);
        setPhase("clarify");
        setInputValue("");
        focusInput();
        return;
      }
    } catch (err) {
      console.warn("clarify unavailable, continuing:", err?.message);
    }
    setClarify(null);
    showQuestion(0, next);
    persist({ phase: "question", stepIndex: 0, answers: next });
  };

  const answerClarify = (text) => {
    const trimmed = (text || "").trim();
    if (!trimmed || !clarify) return;
    const next = {
      ...answers,
      clarifications: [...(answers.clarifications || []), { question: clarify.question, answer: trimmed }],
    };
    setAnswers(next);
    setInputValue("");
    withPause(() => beginClarify(next));
  };

  const submitBrief = () => {
    const trimmed = inputValue.trim();
    if (trimmed.length < 4) return;
    const next = { ...answers, brief: trimmed };
    setAnswers(next);

    withPause(() => {
      if (editingFromSummaryRef.current) {
        editingFromSummaryRef.current = false;
        setInputValue("");
        setPhase("summary");
        persist({ phase: "summary", answers: next, editingFromSummary: false });
        return;
      }
      beginClarify(next);
    });
  };

  const submitFreeText = () => {
    const trimmed = inputValue.trim();
    const q = QUESTIONS[stepIndex];
    if (!q) return;
    const minLen = q.optional ? 0 : 2;
    if (trimmed.length < minLen) return;
    saveAndAdvance(trimmed);
  };

  const chooseChip = (value) => {
    saveAndAdvance(value);
  };

  const skipCurrent = () => saveAndAdvance("");

  const saveAndAdvance = (value) => {
    const q = QUESTIONS[stepIndex];
    if (!q) return;
    const next = { ...answers, [q.key]: value };
    setAnswers(next);

    const wasEditing = editingFromSummaryRef.current;
    editingFromSummaryRef.current = false;

    withPause(() => {
      if (wasEditing) {
        setInputValue("");
        setPhase("summary");
        persist({ phase: "summary", answers: next, editingFromSummary: false });
        return;
      }
      const nextIdx = stepIndex + 1;
      if (nextIdx >= QUESTIONS.length) {
        setInputValue("");
        setPhase("summary");
        persist({ phase: "summary", answers: next });
      } else {
        showQuestion(nextIdx, next);
        persist({ phase: "question", stepIndex: nextIdx, answers: next });
      }
    });
  };

  const onBackOneStep = () => {
    if (phase !== "question") return;
    if (stepIndex === 0) {
      // Return to landing hero with the brief preserved so the client can edit.
      setInputValue(answers.brief || "");
      setProducerVoice("");
      setProducerQuestion("");
      setPhase("landing");
      focusInput();
      return;
    }
    const prevIdx = stepIndex - 1;
    // Rebuild the producer voice for the previous question using the answer that
    // led up to IT (i.e., the answer to the question TWO steps back).
    const prevQ = QUESTIONS[prevIdx];
    const twoBackKey = prevIdx === 0 ? "brief" : QUESTIONS[prevIdx - 1].key;
    const twoBackValue = answers[twoBackKey];
    const line = typeof prevQ.response === "function" ? prevQ.response(twoBackValue) : prevQ.response;
    setProducerVoice(line);
    setProducerQuestion(prevQ.question);
    setStepIndex(prevIdx);
    // Prefill the input with the previous answer so the client can edit.
    const previousAnswer = answers[prevQ.key];
    setInputValue(typeof previousAnswer === "string" ? previousAnswer : "");
    focusInput();
    persist({ phase: "question", stepIndex: prevIdx, answers });
  };

  // Logo click during Discovery. Show a confirmation modal unless we're
  // already on landing or the client has already reached the recommendation.
  useEffect(() => {
    const handler = () => {
      const inConversation =
        phase === "question" ||
        phase === "summary" ||
        phase === "signin" ||
        phase === "preparing" ||
        phase === "error";
      if (inConversation) {
        setHomeConfirmOpen(true);
      } else {
        // Already on landing or done. Reset scroll for a clean feel.
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    };
    window.addEventListener("bnm:home-request", handler);
    return () => window.removeEventListener("bnm:home-request", handler);
  }, [phase]);

  const confirmReturnHome = () => {
    setHomeConfirmOpen(false);
    resetAll();
  };
  const dismissReturnHome = () => setHomeConfirmOpen(false);

  const onEditFromSummary = (key) => {
    editingFromSummaryRef.current = true;
    if (key === "brief") {
      setInputValue(answers.brief || "");
      setPhase("landing");
      focusInput();
      return;
    }
    const idx = QUESTIONS.findIndex((q) => q.key === key);
    if (idx < 0) {
      editingFromSummaryRef.current = false;
      return;
    }
    // Warm producer line when editing.
    producerAckOverrideRef.current = "Sure. Let\u2019s update that.";
    showQuestion(idx, answers);
    persist({ phase: "question", stepIndex: idx, answers, editingFromSummary: true });
  };

  const onContinueFromSummary = () => {
    if (user) {
      setPhase("preparing");
      submitProject(answers);
    } else {
      persist({ phase: "awaiting-signin", answers });
      setPhase("signin");
    }
  };

  const onSignIn = () => {
    try {
      sessionStorage.setItem("bnm.postAuthRedirect", "/");
    } catch {
      // storage unavailable (private mode); persistence is optional
    }
    persist({ phase: "awaiting-signin", answers });
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const onFilesPicked = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 8);
    e.target.value = "";
    for (const f of files) {
      setUploadingCount((c) => c + 1);
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await axios.post(`${API}/brief/upload`, fd);
        setAttachments((a) => [...a, { id: data.id, name: data.name, type: data.type }]);
      } catch (err) {
        console.error("attachment upload failed", err);
      } finally {
        setUploadingCount((c) => c - 1);
      }
    }
  };

  const submitProject = async (finalAnswers) => {
    setError(null);
    try {
      const { data } = await axios.post(`${API}/projects`, {
        brief: finalAnswers.brief,
        concept_ready: finalAnswers.concept_ready,
        timeline: finalAnswers.timeline,
        budget: finalAnswers.budget,
        location: finalAnswers.location,
        notes: finalAnswers.notes,
        attachments: attachments.map((a) => ({ id: a.id, name: a.name, type: a.type })),
        clarifications: finalAnswers.clarifications || [],
        contact: user ? { name: user.name || "", email: user.email || "" } : null,
      });
      const project = data?.project || {};
      setRecommendation(project.recommendation || null);
      setSavedProject(project);
      setPhase("recommendation");
      clearPersisted();
    } catch (err) {
      console.error("save project failed", err);
      setError(err?.response?.data?.detail || "Your Producer is taking a moment. Please try again.");
      setPhase("error");
    }
  };

  const onRetry = () => {
    setPhase("preparing");
    submitProject(answers);
  };
  const onBackToSummary = () => setPhase("summary");

  const resetAll = () => {
    clearPersisted();
    setAnswers({
      brief: "",
      concept_ready: "",
      timeline: "",
      budget: "",
      location: "",
      notes: "",
    });
    setInputValue("");
    setStepIndex(0);
    setRecommendation(null);
    setError(null);
    setProducerVoice("");
    setProducerQuestion("");
    setObservation(null);
    observeTriedRef.current = false;
    setPhase("landing");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ---------- Derived ----------
  const currentQuestion = phase === "question" ? QUESTIONS[stepIndex] : null;
  const showMarketing = phase === "landing"; // fades out after Q1
  const showConversationUI = phase === "landing" || phase === "question" || phase === "clarify";
  const statusText =
    phase === "preparing"
      ? PROCESSING_STATES[processingIndex]
      : "YOUR PRODUCER IS LISTENING";

  // Text size adapts by phase. Discovery has its own restrained scale so
  // questions fit comfortably in 1-2 lines and don't compete with the marketing
  // landing headline. Line height is slightly tighter (~1.1) for balance.
  const heroSizeClass =
    phase === "landing"
      ? "text-[44px] sm:text-[68px] md:text-[88px] lg:text-[100px] leading-[0.94]"
      : phase === "question"
      ? "text-[26px] sm:text-[36px] md:text-[46px] lg:text-[54px] leading-[1.12]"
      : "text-[32px] sm:text-[44px] md:text-[58px] lg:text-[68px] leading-[1.02]";

  return (
    <div className="min-h-screen bg-white text-ink flex flex-col">
      <Nav />

      {/* HERO / WORKSPACE. One anchored slot for the whole conversation. */}
      <section
        className="pt-16 md:pt-24 pb-12 md:pb-16"
        data-testid="hero-workspace"
        data-phase={phase}
      >
        <div className="max-w-[1280px] mx-auto px-4 md:px-8">
          <div className="max-w-[1120px] mx-auto text-center">
            {/* Producer caption slot. During question phase, the producer response line
                lives HERE as a small caption, not inside the H1. Height is reserved so the
                hero doesn't jump between phases with/without a caption. Note: we deliberately
                do NOT repeat "YOUR PRODUCER" here, the status pill below the input is the
                single producer identity for the whole experience. */}
            <div className="mb-6 md:mb-8 min-h-[52px] md:min-h-[60px] flex flex-col items-center justify-end">
              {phase === "landing" && (
                <div className="eyebrow eyebrow-denim eyebrow-soft" data-testid="hero-positioning">
                  Your Producer
                </div>
              )}
              {(phase === "question" || phase === "clarify") && producerVoice && (
                <p
                  key={`voice-${stepIndex}`}
                  className="text-muted-ink text-[13.5px] md:text-[15px] leading-[1.5] max-w-[560px] mx-auto animate-[fadeUp_360ms_ease-out_both]"
                  data-testid="producer-caption"
                >
                  {producerVoice}
                </p>
              )}
            </div>

            {/* HERO H1. On landing this is the marketing headline. During Discovery it is
                ONLY the question (the producer's response line lives in the caption above).
                Keyed by phase + stepIndex + text so React remounts on each turn and we get a
                smooth fade. */}
            <h1
              key={`hero-${phase}-${stepIndex}-${producerQuestion || ""}`}
              data-testid={HOME.heroHeadline}
              className={`animate-[fadeUp_400ms_ease-out_both] font-display font-extrabold text-ink tracking-[-0.03em] max-w-[900px] mx-auto ${heroSizeClass}`}
              data-question={phase === "question" ? currentQuestion?.key : undefined}
            >
              {renderHeroCopy(phase, producerVoice, producerQuestion)}
            </h1>

            {/* CHIP ROW, reserved slot, always present during landing / conversation phases.
                min-height keeps the layout stable when a question has no chips. */}
            {showConversationUI && (
              <div
                className="mt-10 md:mt-14 min-h-[56px] md:min-h-[62px] flex flex-wrap justify-center items-center gap-2.5 md:gap-3"
                data-testid="discovery-chip-slot"
              >
                {phase === "landing" && <HeroIntelligence />}
                {phase === "clarify" && clarify && clarify.options.length > 0 && (
                  <div
                    className="flex flex-wrap justify-center gap-2.5 md:gap-3 animate-[fadeUp_400ms_ease-out_both] w-full"
                    data-testid="clarify-chips"
                  >
                    {clarify.options.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => answerClarify(opt)}
                        className="px-5 md:px-6 py-3 md:py-3.5 rounded-full border border-bnm bg-white text-ink text-[15px] md:text-[16px] transition-all duration-300 hover:-translate-y-0.5 hover:border-black hover:shadow-[0_8px_22px_rgba(10,10,10,0.06)] active:translate-y-0"
                        data-testid="clarify-option-chip"
                        data-value={opt}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                )}
                {phase === "question" && currentQuestion && currentQuestion.chips.length > 0 && (
                  <div
                    className="flex flex-wrap justify-center gap-2.5 md:gap-3 animate-[fadeUp_400ms_ease-out_both] w-full"
                    data-testid="discovery-chips"
                    key={`chips-${stepIndex}`}
                  >
                    {currentQuestion.chips.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => chooseChip(opt.value)}
                        className="px-5 md:px-6 py-3 md:py-3.5 rounded-full border border-bnm bg-white text-ink text-[15px] md:text-[16px] transition-all duration-300 hover:-translate-y-0.5 hover:border-black hover:shadow-[0_8px_22px_rgba(10,10,10,0.06)] active:translate-y-0"
                        data-testid={DISCOVERY.optionChip}
                        data-value={opt.value}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* THE ONE INPUT. Same field on landing and every discovery question.
                On choice-only questions (e.g. Q2 Yes/No) we hide the field entirely so
                the client can't type ambiguous free text like "yes"/"no". A reserved
                slot with matching min-height keeps the status pill anchored. */}
            {showConversationUI && (
              <div className="mt-6 md:mt-8">
                {(() => {
                  const inputHidden =
                    phase === "question" && currentQuestion && !currentQuestion.inputAllowed;
                  if (inputHidden) {
                    // Reserved empty slot so the status pill below doesn't jump up.
                    // Matches the outer height of the idea-shell (px + py + textarea line + arrow).
                    return (
                      <div
                        className="max-w-[820px] mx-auto h-[86px] md:h-[96px]"
                        aria-hidden
                        data-testid="input-hidden-slot"
                      />
                    );
                  }
                  return (
                    <SingleInput
                      ref={inputRef}
                      value={inputValue}
                      onChange={setInputValue}
                      onFocus={() => setFocused(true)}
                      onBlur={() => setFocused(false)}
                      onSubmit={
                        phase === "landing"
                          ? submitBrief
                          : phase === "clarify"
                            ? () => answerClarify(inputValue)
                            : submitFreeText
                      }
                      placeholder={
                        phase === "landing"
                          ? ""
                          : phase === "clarify"
                            ? "Or tell me in your own words"
                            : currentQuestion?.placeholder || ""
                      }
                      disabled={false}
                      showRotator={phase === "landing" && inputValue.length === 0 && !focused}
                      rotatorIndex={phIndex}
                      rotatorFading={phFading}
                      optional={phase === "question" && !!currentQuestion?.optional}
                      onSkip={skipCurrent}
                    />
                  );
                })()}
                {/* Attachments: hand references to Your Producer with the brief. */}
                {phase === "landing" && (
                  <div className="mt-4 flex flex-wrap items-center justify-center gap-2" data-testid="brief-attachments">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      hidden
                      accept="image/*,.pdf,.doc,.docx,.ppt,.pptx,.txt,.zip"
                      onChange={onFilesPicked}
                      data-testid="attach-files-input"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="btn-denim-outline inline-flex items-center gap-2 px-4 py-2 text-[12.5px]"
                      data-testid="attach-files-btn"
                    >
                      <Paperclip className="w-3.5 h-3.5" />
                      Attach files
                    </button>
                    {attachments.map((a) => (
                      <span
                        key={a.id}
                        className="inline-flex items-center gap-1.5 max-w-[220px] px-3 py-1.5 rounded-full border border-bnm bg-white text-[12px] text-ink"
                        data-testid="attachment-chip"
                      >
                        <span className="truncate">{a.name}</span>
                        <button
                          type="button"
                          onClick={() => setAttachments((list) => list.filter((x) => x.id !== a.id))}
                          className="text-muted-ink hover:text-ink transition-colors shrink-0"
                          aria-label={`Remove ${a.name}`}
                          data-testid="attachment-remove"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                    {uploadingCount > 0 && (
                      <span className="text-[12px] text-muted-ink" data-testid="attachment-uploading">
                        Uploading{"\u2026"}
                      </span>
                    )}
                  </div>
                )}
                {/* Status line ALWAYS visible right below the input during landing / question phases */}
                <div className="mt-4">
                  <StatusLine text={statusText} />
                </div>
                {/* Subtle back link. Only visible during discovery questions.
                    Reserved height so its absence on landing doesn't shift things. */}
                <div className="mt-3 h-[22px] flex items-center justify-center">
                  {phase === "question" && (
                    <button
                      type="button"
                      onClick={onBackOneStep}
                      className="inline-flex items-center gap-1.5 text-[12.5px] tracking-[0.02em] text-muted-ink hover:text-ink transition-colors"
                      data-testid={DISCOVERY.backBtn}
                    >
                      <ArrowLeft className="w-3.5 h-3.5" />
                      Back
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* SUMMARY view (replaces the hero slot's supporting UI). */}
            {phase === "summary" && (
              <>
                {observation && (
                  <div className="mt-8 max-w-[600px] mx-auto animate-[fadeUp_400ms_ease-out_both]" data-testid="ida-observation">
                    <div className="bnm-card p-5 md:p-6 text-left border-l-2 border-l-denim-bnm">
                      <div className="eyebrow eyebrow-denim mb-2">Before we go on, a thought</div>
                      <p className="text-ink text-[15px] md:text-[16px] leading-[1.7]">{observation}</p>
                    </div>
                  </div>
                )}
                <SummaryPanel
                  answers={answers}
                  onEdit={onEditFromSummary}
                  onContinue={onContinueFromSummary}
                />
              </>
            )}

            {/* SIGN IN view. */}
            {phase === "signin" && (
              <SignInPanel onSignIn={onSignIn} />
            )}

            {/* PREPARING view. Status pill just below, no big loader. */}
            {phase === "preparing" && (
              <div className="mt-12 md:mt-14">
                <StatusLine text={statusText} />
              </div>
            )}

            {/* RECOMMENDATION view. */}
            {phase === "recommendation" && recommendation && (
              <div className="mt-10 md:mt-14">
                <RecommendationView rec={recommendation} user={user} />
                <div className="mt-14 md:mt-16 flex flex-col sm:flex-row items-center justify-center gap-3">
                  {savedProject?.id && (
                    <button
                      type="button"
                      onClick={() => navigate(`/projects/${savedProject.id}`)}
                      className="btn-denim"
                      data-testid="open-project-workspace"
                    >
                      <span>Open your project workspace</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={resetAll}
                    className="btn-ghost"
                    data-testid="discovery-new-project"
                  >
                    <span>Start another project</span>
                  </button>
                </div>
              </div>
            )}

            {/* ERROR view. */}
            {phase === "error" && (
              <div className="mt-10 max-w-[520px] mx-auto text-center animate-[fadeUp_400ms_ease-out_both]">
                {error && (
                  <p className="text-muted-ink text-[15px] md:text-[16.5px] leading-[1.7]">{error}</p>
                )}
                <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                  <button type="button" onClick={onRetry} className="btn-denim" data-testid="discovery-retry">
                    <span>Try again</span>
                  </button>
                  <button type="button" onClick={onBackToSummary} className="btn-ghost">
                    <span>Back to summary</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Marketing sections. Visible on landing only; fade out for the whole Discovery flow. */}
      <div
        className={`transition-all duration-700 ease-out ${
          showMarketing ? "opacity-100 max-h-[9999px]" : "opacity-0 max-h-0 overflow-hidden pointer-events-none"
        }`}
        aria-hidden={!showMarketing}
        data-testid="marketing-sections"
        data-visible={showMarketing ? "1" : "0"}
      >
        <HomeMarketing startConversation={startConversation} />
      </div>

      <Footer />

      {/* Home confirmation modal. Shown when the client clicks the BRAND NEW
          MONEY logo while mid-conversation. */}
      {homeConfirmOpen && (
        <div
          className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-sm flex items-center justify-center px-6 animate-[fadeUp_220ms_ease-out_both]"
          data-testid="home-confirm-modal"
          onClick={dismissReturnHome}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="w-full max-w-[460px] bg-white border border-bnm rounded-2xl p-8 md:p-10 shadow-[0_24px_60px_rgba(10,10,10,0.18)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="eyebrow eyebrow-denim mb-5">Your Producer</div>
            <h3 className="font-display font-extrabold text-ink text-[26px] md:text-[32px] tracking-[-0.02em] leading-[1.15]">
              Leave this conversation?
            </h3>
            <p className="text-muted-ink text-[15px] md:text-[16.5px] mt-5 leading-[1.7]">
              You have an unfinished project. If you leave now, we won{"\u2019"}t save your answers.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                type="button"
                onClick={dismissReturnHome}
                className="btn-denim w-full sm:flex-1 justify-center"
                data-testid="home-confirm-continue"
              >
                <span>Continue Editing</span>
              </button>
              <button
                type="button"
                onClick={confirmReturnHome}
                className="btn-ghost w-full sm:flex-1 justify-center"
                data-testid="home-confirm-leave"
              >
                <span>Return Home</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Single input (used on landing AND every question) ----------
const SingleInput = React.forwardRef(function SingleInput(
  {
    value,
    onChange,
    onFocus,
    onBlur,
    onSubmit,
    placeholder,
    disabled,
    showRotator,
    rotatorIndex,
    rotatorFading,
    optional,
    onSkip,
  },
  ref,
) {
  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!disabled) onSubmit?.();
    }
  };
  const canSubmit = !disabled && (optional || value.trim().length >= 2);
  return (
    <div className="max-w-[820px] mx-auto">
      <div className="idea-shell px-5 md:px-7 py-5 md:py-6">
        <div className="flex items-end gap-4">
          <div className="relative flex-1">
            <textarea
              ref={ref}
              rows={1}
              value={value}
              onChange={(e) => {
                if (disabled) return;
                onChange(e.target.value);
                e.target.style.height = "auto";
                e.target.style.height = e.target.scrollHeight + "px";
              }}
              onFocus={onFocus}
              onBlur={onBlur}
              onKeyDown={onKey}
              readOnly={disabled}
              placeholder={placeholder || ""}
              className="idea-input idea-input--quiet w-full py-1 max-h-[180px] relative z-[1]"
              data-testid={HOME.ideaInput}
              aria-label={placeholder || "Answer"}
            />
            {showRotator && (
              <div className="idea-placeholder-layer" aria-hidden data-testid="idea-placeholder-rotator">
                <span key={rotatorIndex} className={`idea-placeholder-word ${rotatorFading ? "is-out" : "is-in"}`}>
                  {PLACEHOLDER_EXAMPLES[rotatorIndex]}
                </span>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => !disabled && onSubmit?.()}
            disabled={!canSubmit}
            aria-label="Continue"
            className={`send-btn shrink-0 ${disabled ? "opacity-30 cursor-not-allowed" : ""}`}
            data-testid={HOME.ideaSubmit}
          >
            <ArrowUp className="w-5 h-5" />
          </button>
        </div>
      </div>
      {optional && (
        <div className="mt-3 text-center">
          <button
            type="button"
            onClick={onSkip}
            className="eyebrow eyebrow-muted hover:text-ink transition-colors"
            data-testid="discovery-skip"
          >
            Skip this
          </button>
        </div>
      )}
    </div>
  );
});

// ---------- Summary panel ----------
function SummaryPanel({ answers, onEdit, onContinue }) {
  return (
    <div
      className="mt-12 md:mt-14 max-w-[720px] mx-auto text-left animate-[fadeUp_400ms_ease-out_both]"
      data-testid={DISCOVERY.summary}
    >
      <div className="divide-y divide-[#eaeaea] border-t border-b border-[#eaeaea]">
        {SUMMARY_ROWS.map((row) => (
          <div
            key={row.key}
            className="py-4 md:py-5 flex items-start gap-6"
            data-testid={DISCOVERY.summaryRow}
            data-key={row.key}
          >
            <div className="w-[100px] md:w-[130px] shrink-0 pt-0.5 text-[13px] md:text-[14px] text-muted-ink">
              {row.label}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-ink text-[16px] md:text-[17.5px] leading-[1.55] whitespace-pre-wrap">
                {displayValue(row.key, answers[row.key])}
              </p>
            </div>
            <button
              type="button"
              onClick={() => onEdit(row.key)}
              className="inline-flex items-center gap-2 text-[13px] md:text-[14px] text-muted-ink hover:text-ink transition-colors pt-1 shrink-0"
              data-testid={DISCOVERY.summaryEdit}
              data-key={row.key}
              aria-label={`Edit ${row.label}`}
            >
              <Pencil className="w-3.5 h-3.5" />
              Edit
            </button>
          </div>
        ))}
      </div>
      <div className="mt-8 md:mt-10 flex justify-center">
        <button
          type="button"
          onClick={onContinue}
          className="btn-denim"
          data-testid={DISCOVERY.summaryContinue}
        >
          <span>Continue</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ---------- Sign In panel ----------
function SignInPanel({ onSignIn }) {
  return (
    <div
      className="mt-12 md:mt-14 max-w-[560px] mx-auto text-center animate-[fadeUp_400ms_ease-out_both]"
      data-testid={DISCOVERY.signinWall}
    >
      <p className="text-muted-ink text-[16px] md:text-[17.5px] leading-[1.75]">
        Before we share our recommendation, we need a name and an email so our team can save this project and follow up.
      </p>
      <div className="mt-8 flex justify-center">
        <button type="button" onClick={onSignIn} className="btn-denim" data-testid={DISCOVERY.signinBtn}>
          <span>Sign in</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
      <p className="text-muted-ink text-[12px] mt-6">
        By continuing you agree to be contacted about this project. We don{"\u2019"}t send marketing email.
      </p>
    </div>
  );
}
